import React, { useEffect, useRef, useState, useMemo } from "react";
import { useClickOutside } from "../hooks/useClickOutside";

export interface SearchableDropdownProps {
  /**
   * Whether the dropdown is currently expanded/open
   */
  isExpanded: boolean;
  /**
   * Callback to toggle the dropdown open/closed state
   */
  onToggle: () => void;
  /**
   * List of all available values to display
   */
  values: string[];
  /**
   * Set of currently selected values
   */
  selectedValues: Set<string>;
  /**
   * Callback when a value is selected/deselected
   */
  onSelectValue: (value: string) => void;
  /**
   * Callback when Apply button is clicked
   */
  onApply: () => void;
  /**
   * Callback when Cancel button is clicked
   */
  onCancel: () => void;
  /**
   * Whether data is currently loading
   */
  isLoading?: boolean;
  /**
   * Placeholder text for the search input
   */
  searchPlaceholder?: string;
  /**
   * Message to display when no values are available
   */
  emptyMessage?: string;
  /**
   * Function to generate message when no search results found
   */
  noResultsMessage?: (searchTerm: string) => string;
  /**
   * Minimum width of the dropdown
   */
  minWidth?: string;
  /**
   * Maximum width of the dropdown
   */
  maxWidth?: string;
  /**
   * Maximum height of the values list
   */
  maxHeight?: string;
  /**
   * Whether the Apply button should be enabled
   */
  canApply?: boolean;
  /**
   * Custom render function for the trigger button
   */
  renderTrigger: (props: {
    isExpanded: boolean;
    onClick: () => void;
  }) => React.ReactNode;
  /**
   * Optional render function for selected count display
   */
  renderSelectedCount?: (count: number) => React.ReactNode;
  /**
   * Optional renderer for an option's label (e.g. a tag value's workspace
   * display name). Defaults to the raw value as text; the value itself stays
   * the identity used for selection and matching either way.
   */
  renderOptionLabel?: (value: string) => React.ReactNode;
  /**
   * Optional trailing content for each option row (e.g. how many traces sit
   * behind a value). The row layout stays here so every caller aligns the same
   * way; only the content is the caller's business.
   */
  renderOptionMeta?: (value: string) => React.ReactNode;
  /**
   * Optional muted line above the action buttons — used to say when the list on
   * screen is only part of what exists.
   */
  footerNote?: React.ReactNode;
  /**
   * Optional custom search matching function
   */
  searchMatchFn?: (searchTerm: string, value: string) => boolean;
}

/**
 * Simple fuzzy search function - checks if search term appears in value (case-insensitive)
 */
function defaultFuzzyMatch(searchTerm: string, value: string): boolean {
  if (!searchTerm.trim()) return true;
  const normalizedSearch = searchTerm.toLowerCase().trim();
  const normalizedValue = value.toLowerCase();
  return normalizedValue.includes(normalizedSearch);
}

/**
 * Generic searchable dropdown component with multi-select support.
 * Provides search functionality, viewport-aware positioning, and apply/cancel actions.
 */
export function SearchableDropdown({
  isExpanded,
  onToggle,
  values,
  selectedValues,
  onSelectValue,
  onApply,
  onCancel,
  isLoading = false,
  searchPlaceholder = "Search...",
  emptyMessage = "No items available",
  noResultsMessage = (searchTerm) => `No items match "${searchTerm}"`,
  minWidth = "350px",
  maxWidth = "500px",
  maxHeight = "256px",
  canApply = true,
  renderTrigger,
  renderSelectedCount,
  renderOptionLabel,
  renderOptionMeta,
  footerNote,
  searchMatchFn = defaultFuzzyMatch,
}: SearchableDropdownProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => {
    if (isExpanded) {
      onCancel();
    }
  }, isExpanded);

  const [position, setPosition] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    marginTop?: string;
    marginBottom?: string;
  }>({});

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isExpanded && searchInputRef.current) {
      // Small delay to ensure dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      // Clear search when dropdown closes
      setSearchTerm("");
    }
  }, [isExpanded]);

  // Filter values based on search term
  const filteredValues = useMemo(() => {
    if (!searchTerm.trim()) return values;
    return values.filter((value) => searchMatchFn(searchTerm, value));
  }, [values, searchTerm, searchMatchFn]);

  // Calculate position to keep dropdown within viewport
  useEffect(() => {
    if (!dropdownRef.current || !isExpanded) return;

    const dropdown = dropdownRef.current;
    const parent = dropdown.parentElement;
    if (!parent) return;

    requestAnimationFrame(() => {
      if (!dropdownRef.current) return;

      const rect = parent.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const newPosition: typeof position = {};

      // Check horizontal overflow
      const estimatedWidth = parseInt(minWidth) || 350;
      if (rect.left + estimatedWidth > viewportWidth) {
        newPosition.right = "0";
        newPosition.left = "auto";
      } else {
        newPosition.left = "0";
        newPosition.right = "auto";
      }

      // Check vertical overflow
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const estimatedHeight = Math.min(dropdownRect.height || 400, 400);

      if (spaceBelow < estimatedHeight + 8 && spaceAbove > spaceBelow) {
        newPosition.bottom = "100%";
        newPosition.top = "auto";
        newPosition.marginBottom = "0.5rem";
        newPosition.marginTop = "0";
      } else {
        newPosition.top = "100%";
        newPosition.bottom = "auto";
        newPosition.marginTop = "0.5rem";
        newPosition.marginBottom = "0";
      }

      setPosition(newPosition);
    });
  }, [isExpanded, filteredValues.length, isLoading, minWidth]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  const dropdownBaseClasses =
    "absolute bg-card border border-border rounded-lg shadow-lg z-10";

  const searchInputClasses =
    "w-full px-3 py-2 border-b border-border text-sm focus:outline-none focus:ring-2 focus:ring-ring rounded-t-lg";

  const valueListBaseClasses = "overflow-y-auto";

  const checkboxLabelClasses =
    "flex items-center gap-2 p-2 hover:bg-accent rounded cursor-pointer";

  const buttonBaseClasses = "flex-1 px-4 py-2 rounded text-sm font-medium";

  const cancelButtonClasses = `${buttonBaseClasses} border border-border text-muted-foreground hover:bg-accent`;

  const applyButtonClasses = `${buttonBaseClasses} bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted disabled:cursor-not-allowed`;

  const style: React.CSSProperties = {
    top: position.top,
    bottom: position.bottom,
    left: position.left,
    right: position.right,
    marginTop: position.marginTop,
    marginBottom: position.marginBottom,
    minWidth,
    maxWidth,
  };

  return (
    <div ref={containerRef} className="relative">
      {renderTrigger({ isExpanded, onClick: onToggle })}

      {isExpanded && (
        <div ref={dropdownRef} className={dropdownBaseClasses} style={style}>
          {/* Search Input */}
          <div className="p-2 border-b border-border" onClick={(e) => e.stopPropagation()}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className={searchInputClasses}
            />
            {searchTerm && (
              <div className="text-xs text-muted-foreground mt-1 px-1">
                {filteredValues.length} result{filteredValues.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>

          {/* Filtered Values List */}
          <div className={valueListBaseClasses} style={{ maxHeight }}>
            {isLoading ? (
              <div className="text-sm text-muted-foreground py-4 px-3 text-center">
                Loading…
              </div>
            ) : filteredValues.length > 0 ? (
              filteredValues.map((value) => (
                <label key={value} className={checkboxLabelClasses}>
                  <input
                    type="checkbox"
                    checked={selectedValues.has(value)}
                    onChange={() => onSelectValue(value)}
                    className="rounded"
                  />
                  <span className="text-sm flex-1 min-w-0 break-words">
                    {renderOptionLabel ? renderOptionLabel(value) : value}
                  </span>
                  {renderOptionMeta && (
                    <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
                      {renderOptionMeta(value)}
                    </span>
                  )}
                </label>
              ))
            ) : searchTerm ? (
              <div className="text-sm text-muted-foreground py-4 px-3 text-center">
                {noResultsMessage(searchTerm)}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-4 px-3 text-center">
                {emptyMessage}
              </div>
            )}
          </div>

          {/* Selected Count & Actions */}
          {selectedValues.size > 0 && (
            <div className="px-3 py-2 border-t border-border bg-muted text-xs text-muted-foreground">
              {renderSelectedCount ? (
                renderSelectedCount(selectedValues.size)
              ) : (
                `${selectedValues.size} item${selectedValues.size !== 1 ? "s" : ""} selected`
              )}
            </div>
          )}

          {/* Partial-list notice */}
          {footerNote && !isLoading && (
            <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground">
              {footerNote}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 p-3 border-t border-border">
            <button onClick={onCancel} className={cancelButtonClasses}>
              Cancel
            </button>
            <button onClick={onApply} disabled={!canApply} className={applyButtonClasses}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
